import { removeIncompatibleSearchParameters } from "@helpers/removeIncompatibleSearchParameters"
import { appStructureQueryOptions } from "@redux/features/appStructure/queries"
import { opModesQueryOptions } from "@stateFeatures/opMode/queries"
import { opModeSearchSchema } from "@stateFeatures/opMode/searchSchema"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { CustomerData } from "api"
import { z } from "zod"

const dateSchema = z.preprocess((val) => {
	if (typeof val === "string") {
		const parsedDate = new Date(val)
		if (!isNaN(parsedDate.getTime())) {
			return parsedDate.getTime()
		}
	} else if (typeof val === "number" && !isNaN(val)) {
		return val
	}
	return undefined
}, z.number().int().min(0).optional())

const MIN_YEAR_DATA_DOWNLOAD = 2020
const minDateFrom = new Date(`${MIN_YEAR_DATA_DOWNLOAD}-01-01`).getTime()

export const downloadDataPageSearchSchema = z
	.object({
		vessel: z.optional(z.string()).catch(undefined), //TODO: find a way to get list of vessels
		// opMode: z.optional(z.string()).catch(undefined), //TODO: find a way to get list of opModes
		toDownloadData: dateSchema,
		fromDownloadData: dateSchema,
	})
	.merge(opModeSearchSchema)
	.superRefine((data, ctx) => {
		let hasErrors = false
		if (
			(data.fromDownloadData !== undefined && data.toDownloadData === undefined) ||
			(data.fromDownloadData === undefined && data.toDownloadData !== undefined)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Both 'fromDownloadData' and 'toDownloadData' must be provided together",
				path: ["fromDownloadData"],
			})
			hasErrors = true
		}

		if (
			data.fromDownloadData !== undefined &&
			data.toDownloadData !== undefined &&
			data.fromDownloadData >= data.toDownloadData
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "'fromDownloadData' must be before 'toDownloadData'",
				path: ["fromDownloadData"],
			})
			hasErrors = true
		}

		if (data.fromDownloadData !== undefined && data.fromDownloadData < minDateFrom) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: `'fromDownloadData' cannot be earlier than ${MIN_YEAR_DATA_DOWNLOAD}-01-01`,
				path: ["fromDownloadData"],
			})
			hasErrors = true
		}

		if (hasErrors === true) {
			data.fromDownloadData = undefined
			data.toDownloadData = undefined
		}
	})

export const validateDownloadDataSearchParams = (
	inputParams: Record<string, unknown>
): z.infer<typeof downloadDataPageSearchSchema> => {
	const result = downloadDataPageSearchSchema.safeParse(inputParams)

	if (result.success === false) {
		return {
			...inputParams,
			opMode: "all",
			fromDownloadData: undefined,
			toDownloadData: undefined,
		}
	}

	return { ...inputParams, ...result.data }
}

const findPlantData = (
	appStructure: { [key: string]: CustomerData } | null,
	companyName: string | undefined,
	vesselName: string | undefined
) => {
	if (appStructure == null) {
		return undefined
	}
	if (companyName === undefined || vesselName === undefined) {
		return undefined
	}
	if (Object.keys(appStructure)[0] === "Dipai") {
		return appStructure["Dipai"]?.clients[companyName]?.plants[vesselName]
	} else {
		return appStructure[companyName]?.plants[vesselName]
	}
}
export const Route = createFileRoute("/clients/$clientName/_sopPageLayout/download-data")({
	component: RouteComponent,
	validateSearch: validateDownloadDataSearchParams,
	loaderDeps: ({ search }) => ({ search }),
	loader: async ({ context, deps, params }) => {
		const { queryClient, store } = context
		const { opMode, vessel } = deps.search

		if (vessel === undefined) {
			return
		}
		const appStructure = await queryClient.fetchQuery(appStructureQueryOptions())
		// 1) Resolve vesselId from vesselName (shared prerequisite)
		const currentCompany =
			params.clientName ?? (appStructure != null ? Object.keys(appStructure)[0] : undefined)
		const plantData = findPlantData(appStructure, currentCompany, vessel)
		if (plantData === undefined) {
			return
		}
		// 2) Prefetch op modes (async filter options)
		const opModes = await queryClient.fetchQuery(
			opModesQueryOptions({ plantIdsList: [plantData?.id ?? -1] })
		)
		if (opModes == null || opModes.length === 0) throw new Error("No op modes for vessel")

		// 3) Default = "first": canonicalize opMode into URL if missing/invalid
		const urlOpMode = opMode
		const urlValid =
			urlOpMode != undefined &&
			opModes.some(
				(m) =>
					m.name.toLowerCase().replaceAll(" ", "") ===
					urlOpMode.toLowerCase().replaceAll(" ", "")
			)
		const effective = urlValid === true ? urlOpMode : (opModes[0]?.name ?? "all")

		if (urlValid === false || urlOpMode === undefined) {
			throw redirect({
				to: Route.fullPath,
				params: { clientName: params.clientName },
				search: (prev) => ({ ...prev, opMode: effective }),
				replace: true,
			})
		}
	},
	search: {
		middlewares: [removeIncompatibleSearchParameters(downloadDataPageSearchSchema)],
	},
})

function RouteComponent() {
	return null
}
